import { deserialize, DocumentObject, serialize } from '@jsonapiworld/jsonapi-serializer';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { parseISO } from 'date-fns';
import sortBy from 'lodash/sortBy';
import { sendEmailOtpChallenge, verifyEmailOtpChallenge } from '~/api/email-otp';
import { challengeTotp } from '~/api/totp';
import { queryClient } from '~/client/queryClient';
import { identify, track } from '~/hooks/segment';
import { notifyAuthChanged } from '~/hooks/useAuthSync';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { Company, CompanyQueryKeys, LeadSource } from '~/types/company';
import {
  UserData,
  LoginMfaChallengeResponse,
  LoginResponse,
  LoginResult,
  LoginTokenResponse,
  MfaMethod,
  UserQueryKeys,
  UserWithCompanies,
} from '~/types/user';
import { setReferralCodeCookie } from '~/utils/cookie';
import { buildJsonAPIQueryParams, jsonAPIIncludeBuilder } from '~/utils/jsonapi-query';
import { clearActivePasskeyId } from '~/utils/passkey';
import { parseRegistrationToken } from '~/utils/registrationToken';
import { AnalyticsEvents } from './analytics';
import { backendApiGateway } from '../client/backend';

export const fetchUser = async (companiesSubscriptions?: boolean): Promise<UserWithCompanies> => {
  const includeBuilder = jsonAPIIncludeBuilder([
    'companies',
    'companies.companyIdentity',
    'companies.companySettings',
    ...(companiesSubscriptions ? ['companies.subscriptions'] : []),
  ]);
  const params = buildJsonAPIQueryParams(includeBuilder);
  const { data } = await backendApiGateway.get<DocumentObject>('api/v1/users/me', {
    params,
  });

  const user = deserialize<UserWithCompanies>(data) as UserWithCompanies;
  user.companies = sortBy(user.companies, (c) => c.name.toLowerCase());

  queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.allCompanies });

  return user;
};

// Post-login side effects, shared by every path that ends authenticated: a
// plain (no-MFA) login and an MFA challenge (phase 2) both converge here. The
// session cookie is already set by the backend at this point; the notifications
// token has already been cached by the caller. A failed user fetch is logged
// but not fatal (matches the pre-existing behavior).
const finalizeSession = async (method: MfaMethod | 'password', logError: (err: unknown) => void): Promise<void> => {
  await queryClient.invalidateQueries();
  clearActivePasskeyId();

  let user: UserWithCompanies | undefined;
  try {
    user = await fetchUser();
    queryClient.setQueryData([...UserQueryKeys.currentUser, false], user);
    notifyAuthChanged(user.id);
    if (user.referralCode) {
      setReferralCodeCookie(user.referralCode);
    }
  } catch (err) {
    logError(err);
  }

  track(AnalyticsEvents.SIGNED_IN, user, null, { type: 'credentials', method });

  if (user) {
    const userCreatedAt = parseISO(user.createdAt);
    // Identify user for Customer.io (in-app messages)
    window._cio.identify({
      id: user.email,
      created_at: userCreatedAt.getTime() / 1000.0,
      first_name: user.firstName,
      last_name: user.lastName,
    });
  }
};

const isMfaChallengeResponse = (data: LoginResponse): data is LoginMfaChallengeResponse => 'mfaRequired' in data;

const login = async (values: { username: string; password: string }): Promise<LoginResult> => {
  const { data } = await backendApiGateway.post<{}, AxiosResponse<LoginResponse>>('api/auth/login', values, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  if (isMfaChallengeResponse(data)) {
    // Phase 2: the backend issued no session/token — the login completes only
    // after an MFA challenge validated with this short-lived pre-auth token.
    return { status: 'mfa_required', mfaToken: data.mfaToken, methods: data.methods };
  }

  queryClient.setQueryData(UserQueryKeys.notificationsToken, data.token);
  return { status: 'authenticated' };
};

const signUp = async (values: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  termsAndConditionsAgreed: boolean;
  privacyPolicyAgreed: boolean;
  marketingAgreed: boolean;
  token: string;
}) => {
  // The newsletter consent was removed from the UI: new users are always registered with it granted,
  // so users with it set to false remain identifiable as those who explicitly declined it in the past.
  const {
    data: { token },
  } = await backendApiGateway.post<{}, AxiosResponse<LoginTokenResponse>>(
    'api/auth/register',
    { ...values, newsletterAgreed: true },
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );
  queryClient.setQueryData(UserQueryKeys.notificationsToken, token);
};

const resetPassword = async (values: { email: string }) => {
  await backendApiGateway.post<{}>('api/auth/reset-password', values, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
};

const changePassword = async (values: { token: string; password: string }) => {
  await backendApiGateway.post<{}>('api/auth/change-password', values, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
};

const loggedUserQueryOptions = (companiesSubscriptions: boolean) => ({
  queryKey: [...UserQueryKeys.currentUser, companiesSubscriptions],
  staleTime: Infinity,
  queryFn: () => fetchUser(companiesSubscriptions),
});

const updateUserAgreements = async (values: {
  /** Required agreements can only be granted, never revoked: the API rejects `false`. Omit to leave untouched. */
  termsAndConditionsAgreed?: true;
  privacyPolicyAgreed?: true;
  marketingAgreed?: boolean;
}) => {
  // The newsletter consent was removed from the UI: it is always granted here,
  // so users with it set to false remain identifiable as those who explicitly declined it in the past.
  const payload = serialize({ ...values, newsletterAgreed: true }, 'user');
  await backendApiGateway.patch<{}>('api/v1/users/me/agreements', payload);
};

export const useUserAgreementsUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUserAgreements,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser }),
  });
};

export const useLoggedUser = (companiesSubscriptions?: boolean) =>
  useQuery(loggedUserQueryOptions(companiesSubscriptions ?? false));

export const useSuspenseLoggedUser = () => useSuspenseQuery(loggedUserQueryOptions(false));

export const useLoginMutation = () => {
  const { captureException } = useExceptionLogger();

  return useMutation({
    mutationFn: login,
    onSuccess: async (result, params) => {
      // Phase 2: an MFA challenge is pending — the user is NOT authenticated
      // yet, so we defer every post-login side effect until the challenge
      // completes (see the challenge mutations below).
      if (result.status !== 'authenticated') return;

      await finalizeSession('password', (err) =>
        captureException(err, {
          extra: { username: params.username, help_text: 'error fetching user after login' },
        }),
      );
    },
  });
};

/**
 * Completes the login via a TOTP challenge (phase 2). On success the backend
 * sets the session cookie and returns `{ token }`; we cache the notifications
 * token and run the shared post-login side effects, so the app proceeds exactly
 * as after a normal login.
 */
export const useTotpChallengeMutation = () => {
  const { captureException } = useExceptionLogger();

  return useMutation({
    mutationFn: ({ code, mfaToken }: { code: string; mfaToken: string }) => challengeTotp(code, mfaToken),
    onSuccess: async (result) => {
      queryClient.setQueryData(UserQueryKeys.notificationsToken, result.token);
      await finalizeSession('totp', (err) =>
        captureException(err, { extra: { help_text: 'error fetching user after totp challenge' } }),
      );
    },
  });
};

/**
 * Sends the login-challenge email code (phase 2). 429 (cooldown) / 401
 * (mfa_token expired) are expected states handled inline by the view.
 */
export const useSendEmailOtpChallengeMutation = () =>
  useMutation({ mutationFn: (mfaToken: string) => sendEmailOtpChallenge(mfaToken) });

/** Completes the login via an email-OTP challenge (phase 2). See TOTP variant. */
export const useVerifyEmailOtpChallengeMutation = () => {
  const { captureException } = useExceptionLogger();

  return useMutation({
    mutationFn: ({ code, mfaToken }: { code: string; mfaToken: string }) => verifyEmailOtpChallenge(code, mfaToken),
    onSuccess: async (result) => {
      queryClient.setQueryData(UserQueryKeys.notificationsToken, result.token);
      await finalizeSession('email_otp', (err) =>
        captureException(err, { extra: { help_text: 'error fetching user after email otp challenge' } }),
      );
    },
  });
};

export const useSignUpMutation = () => {
  const queryClient = useQueryClient();
  const { captureException } = useExceptionLogger();

  return useMutation({
    mutationFn: signUp,
    onSuccess: async (_, params) => {
      await queryClient.invalidateQueries();

      let user;

      try {
        user = await fetchUser();
        queryClient.setQueryData([...UserQueryKeys.currentUser, false], user);
        notifyAuthChanged(user.id);
        if (user.referralCode) {
          setReferralCodeCookie(user.referralCode);
        }
      } catch (err) {
        captureException(err, {
          extra: {
            email: params.email,
            first_name: params.firstName,
            last_name: params.lastName,
            help_text: 'error fetching user after registration',
          },
        });
        user = undefined;
      }
      identify(user);
      const leadSource = parseRegistrationToken(params.token)?.source;
      track(AnalyticsEvents.SIGNED_UP, user, null, {
        leadSource,
        context: leadSource === LeadSource.FREE_INVOICING ? 'self-onboarding' : undefined,
      });
    },
  });
};

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: resetPassword,
  });
};
export const useChangePasswordMutation = () => {
  return useMutation({
    mutationFn: changePassword,
  });
};

const fetchNotificationsToken = async (): Promise<string> => {
  const {
    data: { token },
  } = await backendApiGateway.get<{}, AxiosResponse<LoginTokenResponse>>('api/v1/users/token', {
    headers: {
      Accept: 'application/json',
    },
  });
  return token;
};

export const useNotificationsToken = () => {
  const userQuery = useLoggedUser();
  return useQuery({
    enabled: userQuery.status === 'success',
    queryFn: fetchNotificationsToken,
    queryKey: UserQueryKeys.notificationsToken,
    staleTime: Infinity,
  });
};

export interface InvitedUser {
  companyName: string;
  firstName: string;
  email: string;
  lastName: string;
  taxNumber: string;
  vatNumber: string;
  phone: string | null;
  hasUserBankAccount: boolean;
}

const fetchInvitedUser = async (token: string): Promise<InvitedUser> => {
  const { data: user } = await backendApiGateway.get<{}, AxiosResponse<InvitedUser>>(
    `api/auth/invited-user?token=${token}`,
    {
      headers: {
        Accept: 'application/json',
      },
    },
  );
  return user;
};

export const useInvitedUser = (token: string) => {
  return useQuery({
    queryFn: () => fetchInvitedUser(token),
    queryKey: UserQueryKeys.invitedUser(token),
    staleTime: Infinity,
  });
};

interface AcceptInviteRequest {
  token: string;
  values: UserData;
  taxNumber: string;
  vatNumber: string;
}

const acceptInvite = async ({ token, values }: AcceptInviteRequest) => {
  const {
    data: { token: notificationsToken },
  } = await backendApiGateway.post<{}, AxiosResponse<LoginTokenResponse>>(
    'api/auth/invited-user',
    { ...values, phone: values.phone === undefined ? undefined : values.phone.dialCode + values.phone.number, token },
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );
  queryClient.setQueryData(UserQueryKeys.notificationsToken, notificationsToken);
};

export const useAcceptInvite = () => {
  const queryClient = useQueryClient();
  const { captureException } = useExceptionLogger();

  return useMutation({
    mutationFn: acceptInvite,
    onSuccess: async (_, { values, taxNumber, vatNumber }) => {
      await queryClient.invalidateQueries();

      let user;

      try {
        user = await fetchUser();
        queryClient.setQueryData([...UserQueryKeys.currentUser, false], user);
        notifyAuthChanged(user.id);
      } catch (err) {
        captureException(err, {
          extra: {
            email: values.email,
            first_name: values.firstName,
            last_name: values.lastName,
            help_text: 'error fetching user after registration',
          },
        });
        user = undefined;
      }
      identify(user);
      track(AnalyticsEvents.SIGNED_UP, user, null, {
        context: 'invite',
        invitedBy: {
          taxNumber,
          vatNumber,
        },
      });
    },
  });
};

const verifyEmail = async (payload: { email: string; source?: Company['leadSource'] }) => {
  await backendApiGateway.post<{}>('api/auth/verify-email', payload, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
};

export const useVerifyEmail = () => {
  return useMutation({ mutationFn: verifyEmail });
};
